import {createAction, createAsyncThunk} from '@reduxjs/toolkit';
import {v4 as uuidV4} from 'uuid';

import {type CompPassRecipient} from 'api/comp-passes/comp-pass.types';
import formatPhoneNumber from 'utils/format-phone-number';
import {transformYupErrorsIntoObject} from 'utils/validators';
import * as YupDefault from 'yup';

import {getCompPassRecipientValidationSchema} from 'pages/comp-pass/comp-pass-validation-schemas';
import {
    type CompPassCsvRecipient,
    type FormErrorObject,
    type IndividualError,
    type RecipientError,
} from 'pages/comp-pass/components/comp-pass-wizard/comp-pass-wizard.types';
import {PASS_TYPE} from 'pages/comp-pass/components/comp-pass-wizard/utils';
import {compPassRecipient} from 'pages/comp-pass/store/comp-pass-adapters';
import {selectCompPassType} from 'pages/comp-pass/store/comp-pass-selectors';
import {selectStep1SelectionCount} from 'pages/comp-pass/store/transient-time-frame-selectors';
import {type RootState} from 'store/store';

interface CompPassCsvCreationArgs {
    fileName: string;
    recipients: CompPassCsvRecipient[] | CompPassRecipient[];
}

interface CompPassManualCreationArgs {
    recipients: CompPassRecipient[];
}

interface CompPassRecipientReturn {
    fileName: string;
    recipients: CompPassRecipient[];
}

interface CompPassCreationThunkAPI {
    rejectValue: FormErrorObject;
    state: RootState;
}

interface DeleteRecipientArgs {
    id: string;
    index: number;
}

interface DeleteRecipientReturn {
    recipients: CompPassRecipient[];
    addRecipientsErrors: RecipientError;
}

interface FilterFailedValidationsArgs {
    errors: YupDefault.ValidationError;
    recipients: CompPassRecipient[];
}

const removeDeletedError = ({
    addRecipientsErrors,
    index,
}: {
    addRecipientsErrors: RecipientError;
    index: number;
}) => {
    let updatedErrors = addRecipientsErrors;

    // Check if the specific index exists in the errors object
    if (addRecipientsErrors && addRecipientsErrors[index]) {
        // Destructure to exclude the specific index, keeping all other properties
        // eslint-disable-next-line no-unused-vars
        const {[index]: _, ...remainingErrors} = addRecipientsErrors;

        // If no errors remain, set to null; otherwise use the remaining errors
        updatedErrors =
            Object.keys(remainingErrors).length === 0 ? null : remainingErrors;
    }

    return updatedErrors;
};

/**
 * Thunk to delete a recipient from the comp pass creation state and remove any associated errors.
 *
 * Action Type: compPass/deleteRecipient
 *
 */
export const deleteRecipient = createAsyncThunk<
    DeleteRecipientReturn,
    DeleteRecipientArgs,
    CompPassCreationThunkAPI
>('compPass/deleteRecipient', ({id, index}, {getState, rejectWithValue}) => {
    try {
        const {
            addRecipients: {recipients},
            errors: {addRecipients: addRecipientsErrors},
        } = getState().compPass.passCreation;

        const nextState = compPassRecipient.removeOne(recipients, id);

        const nexRecipients = compPassRecipient
            .getSelectors()
            .selectAll(nextState);

        const updatedErrorsAfterDeletion = removeDeletedError({
            addRecipientsErrors,
            index,
        });

        return {
            recipients: nexRecipients,
            addRecipientsErrors: updatedErrorsAfterDeletion,
        };
    } catch (error) {
        return rejectWithValue(error);
    }
});

/**
 * Action to delete the uploaded CSV file from the comp pass creation state.
 */
export const deleteUploadedCsvFile = createAction(
    'compPass/deleteUploadedCsvFile'
);

/**
 * Filters out the failed validations from the comp pass recipients and returns the passed validations.
 *
 * @param errors - The validation errors from the comp pass recipients.
 * @param recipients - The comp pass recipients to be validated.
 * @returns An object containing the formatted errors and an array of recipients that passed the validation.
 */
const filterFailedValidations = ({
    errors,
    recipients,
}: FilterFailedValidationsArgs) => {
    const formattedErrors = transformYupErrorsIntoObject(errors);
    const failedValidationIndexes = Object.keys(formattedErrors);
    const passedValidations = recipients.filter((_, index) => {
        return !failedValidationIndexes.includes(index.toString());
    });

    return {
        formattedErrors,
        passedValidations,
    };
};

/**
 * Normalizes recipient quantities to numbers. Manual-entry rows arrive with the
 * quantity as a string from the form inputs, but the rest of the app treats
 * quantity as a number, so we convert once here at the store boundary.
 *
 * @param recipients - The recipients whose quantities should be normalized.
 * @returns The recipients with numeric quantities.
 */
const normalizeRecipientQuantities = (
    recipients: CompPassRecipient[]
): CompPassRecipient[] => {
    const normalizedRecipients = recipients.map((recipient) => ({
        ...recipient,
        quantity: Number(recipient.quantity),
    }));

    return normalizedRecipients;
};

/**
 * Thunk to update and validate the comp pass recipients. If any validations fail, it returns the passed validations along with the formatted errors.
 *
 * Action Type: compPass/updateAndValidateManualCompPassRecipients
 *
 */
export const updateAndValidateManualCompPassRecipients = createAsyncThunk<
    CompPassRecipient[],
    CompPassManualCreationArgs,
    {
        state: RootState;
        rejectValue: {errors: FormErrorObject; recipients: CompPassRecipient[]};
    }
>(
    'compPass/updateAndValidateManualCompPassRecipients',
    async ({recipients}, {getState, rejectWithValue}) => {
        try {
            const selectionCount = selectStep1SelectionCount(getState());
            const isTransientFlow =
                selectCompPassType(getState()) === PASS_TYPE.TRANSIENT;

            await getCompPassRecipientValidationSchema(
                isTransientFlow
            ).validate(recipients, {
                abortEarly: false,
                context: {selectionCount},
            });

            return normalizeRecipientQuantities(recipients);
        } catch (error) {
            if (error.name === 'ValidationError') {
                const {formattedErrors, passedValidations} =
                    filterFailedValidations({errors: error, recipients});

                return rejectWithValue({
                    recipients: normalizeRecipientQuantities(passedValidations),
                    errors: formattedErrors,
                });
            }

            return rejectWithValue(error);
        }
    }
);

/**
 * Formatting function for the comp pass recipients from the CSV file.
 *
 * @param recipients - The comp pass recipients from the CSV file.
 * @returns An array of formatted comp pass recipients.
 */
const formatCsvCompPassRecipients = (
    recipients: CompPassCsvRecipient[] | CompPassRecipient[]
): CompPassRecipient[] => {
    const formattedRecipients = recipients.map(
        (recipient: CompPassRecipient | CompPassCsvRecipient) => {
            const formattedRecipient = {
                email: recipient.email.toLowerCase(),
                ccEmail: recipient.ccEmail.toLowerCase(),
                id: uuidV4(),
                licensePlate: recipient.licensePlate.trim().toUpperCase(),
                phoneNumber: formatPhoneNumber(recipient.phoneNumber),
                quantity: Number(recipient.quantity),
            };

            return formattedRecipient;
        }
    );

    return formattedRecipients;
};

/**
 * Thunk to validate and add the comp pass recipients from the CSV file.
 *
 * Action Type: compPass/validateAndAddCsvCompPassRecipients
 *
 */
export const validateAndAddCsvCompPassRecipients = createAsyncThunk<
    CompPassRecipientReturn,
    CompPassCsvCreationArgs,
    CompPassCreationThunkAPI
>(
    'compPass/validateAndAddCsvCompPassRecipients',
    async ({recipients, fileName}, {getState, rejectWithValue}) => {
        try {
            const selectionCount = selectStep1SelectionCount(getState());
            const isTransientFlow =
                selectCompPassType(getState()) === PASS_TYPE.TRANSIENT;
            const formattedRecipients = formatCsvCompPassRecipients(recipients);

            await getCompPassRecipientValidationSchema(
                isTransientFlow
            ).validate(formattedRecipients, {
                abortEarly: false,
                context: {selectionCount},
            });

            return {recipients: formattedRecipients, fileName};
        } catch (error) {
            const formattedErrors = transformYupErrorsIntoObject(error);

            return rejectWithValue(formattedErrors);
        }
    }
);

/**
 * Action to set the error for the CSV upload.
 */
export const setCsvUploadError = createAction<
    FormErrorObject | IndividualError
>('compPass/setCsvUploadError');

/**
 * Action to set whether the disclosure has been acknowledged by the user.
 */
export const setDisclosureAcknowledged = createAction<boolean>(
    'compPass/setDisclosureAcknowledged'
);
